import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Plus, Trash, PencilSimple, FloppyDisk, X, Tag } from "@phosphor-icons/react";

export function ViaticosVisitTypes() {
  const { token } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/viaticos/visit-types`, { headers });
      setTypes(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newName.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/viaticos/visit-types`, { name: newName.trim() }, { headers });
      toast.success("Tipo de visita creado");
      setNewName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API}/viaticos/visit-types/${id}`, { name: editName.trim() }, { headers });
      toast.success("Actualizado");
      setEditId(null);
      load();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este tipo de visita?")) return;
    try {
      await axios.delete(`${API}/viaticos/visit-types/${id}`, { headers });
      toast.success("Eliminado");
      load();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4" data-testid="viaticos-visit-types">
      <h1 className="text-2xl font-bold text-slate-900">Tipos de Visita</h1>

      {/* Add form */}
      <div className="flex gap-2 items-end">
        <div className="flex-1 max-w-sm">
          <label className="text-xs font-medium text-slate-500">Nuevo Tipo</label>
          <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()}
            placeholder="Ej: Mantenimiento" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="visit-type-input" />
        </div>
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="visit-type-add">
          <Plus size={16} /> Agregar
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : types.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No hay tipos de visita</p>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="divide-y">
            {types.map(t => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                {editId === t.id ? (
                  <div className="flex gap-2 items-center flex-1">
                    <input value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleUpdate(t.id)}
                      className="px-3 py-1.5 border rounded-lg text-sm flex-1 max-w-xs" autoFocus data-testid={`visit-type-edit-input-${t.id}`} />
                    <button onClick={() => handleUpdate(t.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"><FloppyDisk size={16} /></button>
                    <button onClick={() => setEditId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X size={16} /></button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Tag size={16} className="text-blue-500" />
                      <span className="text-sm font-medium text-slate-700">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setEditId(t.id); setEditName(t.name); }} className="p-1.5 text-slate-400 hover:text-blue-600 rounded" data-testid={`visit-type-edit-${t.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded" data-testid={`visit-type-delete-${t.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
